import * as React from 'react';
import { storyblokEditable, StoryblokRichText, type SbBlokData } from '@storyblok/react';

import type { ImageContentStoryblok } from '../../types/storyblok';

interface ImageContentProps {
  blok: ImageContentStoryblok;
}

type ContentPosition = 'left' | 'right';

// Desktop-only ordering (mobile always stacks image on top)
const contentOrderClass: Record<ContentPosition, string> = {
  left: 'desktop:order-1',
  right: 'desktop:order-2',
};

const imageOrderClass: Record<ContentPosition, string> = {
  left: 'desktop:order-2',
  right: 'desktop:order-1',
};

// Desktop: Border radius based on image position (inner edge faces content)
// Mobile: 8px all corners (applied separately)
// Above 1440px canvas: all corners rounded so the image reads as a self-contained
// card instead of leaving a sharp edge floating away from the viewport.
const imageRadiusClass: Record<ContentPosition, string> = {
  left: 'desktop:rounded-tl-[64px] desktop:rounded-bl-[64px] desktop:rounded-tr-none desktop:rounded-br-none min-[1441px]:rounded-[64px]',
  right:
    'desktop:rounded-tr-[64px] desktop:rounded-br-[64px] desktop:rounded-tl-none desktop:rounded-bl-none min-[1441px]:rounded-[64px]',
};

// Desktop section padding based on image position (image bleeds to edge)
// Mobile: symmetric padding
// Above 1440px canvas: restore symmetric padding so the image stops bleeding to
// the section edge (which no longer touches the viewport once the canvas is capped).
const sectionPaddingClass: Record<ContentPosition, string> = {
  left: 'px-4 desktop:pl-20 desktop:pr-0 min-[1441px]:pr-20',
  right: 'px-4 desktop:pr-20 desktop:pl-0 min-[1441px]:pl-20',
};

// Content column padding to maintain proper spacing from edge
const contentPaddingClass: Record<ContentPosition, string> = {
  left: '',
  right: 'desktop:pr-0',
};

const normalizePosition = (value: ImageContentStoryblok['content_position']): ContentPosition =>
  value === 'left' ? 'left' : 'right';

const buildSrc = (asset: ImageContentStoryblok['image']): string | undefined =>
  asset?.filename || undefined;

/**
 * ImageContent block renders an image alongside text content.
 *
 * Two variations based on presence of eyebrow:
 * 1. No eyebrow: Single title with brand-deep color
 * 2. With eyebrow: Eyebrow label + title with brand-mid color
 *
 * Content position determines which side the text content appears on.
 */
export const ImageContent: React.FC<ImageContentProps> = ({ blok }) => {
  const headlineId = React.useId();
  const position = normalizePosition(blok.content_position);
  const hasEyebrow = Boolean(blok.eyebrow?.trim());
  const imageSrc = buildSrc(blok.image);
  const imageAlt = blok.image?.alt ?? '';

  // Title color changes based on variation:
  // - No eyebrow: brand-deep (dark blue)
  // - With eyebrow: brand-mid (lighter blue)
  const titleColorClass = hasEyebrow ? 'text-brand-mid' : 'text-brand-deep';

  return (
    <section
      {...storyblokEditable(blok as unknown as SbBlokData)}
      aria-labelledby={headlineId}
      className={`py-10 desktop:py-[214px] max-w-[1440px] mx-auto ${sectionPaddingClass[position]}`}
    >
      <div className="flex flex-col items-center gap-8 desktop:flex-row desktop:items-center desktop:justify-between desktop:gap-28">
        {/* Image column - Mobile: first (centered with gradient), Desktop: edge-bleed */}
        <div className={['relative w-full desktop:w-[599px]', imageOrderClass[position]].join(' ')}>
          {imageSrc && (
            <div className="relative mx-auto w-[232px] h-[232px] desktop:w-full desktop:h-auto desktop:mx-0">
              <img
                src={imageSrc}
                alt={imageAlt}
                className={[
                  'w-full h-full object-cover',
                  'rounded-lg desktop:rounded-none', // 8px mobile, reset for desktop
                  'shadow-[0_4px_32px_0_rgba(47,143,232,0.15)] desktop:shadow-[0_4px_64px_0_rgba(28,43,58,0.16)]',
                  imageRadiusClass[position],
                ].join(' ')}
              />
              {/* Gradient overlay - mobile only */}
              <div
                className="absolute inset-0 rounded-lg pointer-events-none desktop:hidden"
                style={{
                  background:
                    'linear-gradient(179.92deg, rgba(47, 143, 232, 0) 39.022%, rgba(0, 29, 136, 0.88) 105.61%)',
                }}
                aria-hidden="true"
              />
            </div>
          )}
        </div>

        {/* Text content column */}
        <div
          className={[
            'flex flex-col gap-4 desktop:gap-6 w-full desktop:w-[539px] text-center desktop:text-left',
            contentOrderClass[position],
            contentPaddingClass[position],
          ].join(' ')}
        >
          {hasEyebrow && (
            <p className="text-callout font-extrabold uppercase tracking-[0.16em] text-brand-mid">
              {blok.eyebrow}
            </p>
          )}
          <h2 id={headlineId} className={`text-h4 font-extrabold ${titleColorClass}`}>
            {blok.title}
          </h2>
          {blok.content && (
            <div className="text-body1 text-gray-700 image-content-richtext">
              <StoryblokRichText
                doc={blok.content as unknown as Parameters<typeof StoryblokRichText>[0]['doc']}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
